import { create } from 'zustand';
import {
  UploadApiError,
  abortUpload,
  completeUpload,
  createDraftImage,
  createDraftStudy,
  deleteDraftImage,
  expectedPartBytes,
  fetchPartUrls,
  fetchParts,
  fetchStudyImages,
  initiateImageUpload,
  patchDraftImage,
  patchStudyDetails,
  putPart,
  touchUpload,
  dismissActiveUpload,
  fetchUploadRoom,
  type ActiveUpload,
  type DraftImage,
  type ImageDetails,
  type ImageStage,
  type StudyDetails,
} from '../../features/upload/api';
import { deleteStudy } from '../../features/collections/api';
import { certainKind, defaultImageType, isImageType, isLaterality, lateralityOfCode, typeAllowed, typeFits, type ImageKind, type ImageType, type Laterality } from '../../data/imageTypes';
import { UPLOAD_SESSION_KEY, uploadStorageCleared } from '../../features/upload/storageKeys';
import { forgetFile, keepFile, keepSlide, keptFiles, pathOf, reopenFiles, type KeptFile, type Reopened } from '../../features/upload/fileHandles';
import { bytesOf, isSlide, isXray, type Picked } from '../../features/upload/dicomSlides';
import { holdsOf, sameMembers, tarOf, type TarMember } from '../../features/upload/tar';
import { markLeft, unmarkLeft } from '../../features/upload/leftUploads';
import { Semaphore, abortError, guardStall, isAbort, isTransient, sleep, withRetry } from '../../features/upload/transfer';
import { toast } from '../ui/toast';
import { UPLOAD_LIST_KEY, invalidateCatalogue, refreshPublished, refreshUploadList } from '../api/freshness';
import { queryClient } from '../../hooks/queryClient';
import { imageNameOf } from '../../features/upload/imageName';
import { findIdentifierLikeText } from './phiGuard';
import { SITE_SCHEME, STAIN_SCHEME } from '../data/codes';

/**
 * The community uploader's state: one SESSION per study being uploaded into,
 * each the study step and the files step of the two-step flow, over the
 * server's DRAFTS (server/src/http/drafts.ts). Starting another study never
 * stops one in flight: every file of every session sends from ONE pool of
 * connections, served in the order the files were added (partGate), so the
 * first study's bytes land whole and it starts converting while the next one
 * is still sending. The pages name the session they show (step 1 by its id,
 * the files page by its study); the top bar lists them all (UploadPill).
 *
 * The study and every image are rows on the server before a byte is sent:
 * step 1's Continue makes the study (or names an existing one), adding a file
 * makes its image and then sends the bytes (multipart, the same transport the
 * enterprise page uses), and each detail is saved to the image as it is typed.
 * The server converts an image as soon as its bytes are in and publishes it
 * once it is indexed AND has its anatomic site, so nothing waits for a final
 * click: Finish only moves to the progress page. The session polls the study
 * until every image it added is settled, wherever the person is, so the pill
 * and the "ready" toast work anywhere in the shell.
 *
 * The session outlives the page: navigating away keeps every transfer running
 * (the top bar shows a pill that leads back), and a reload keeps it too, minus
 * the File objects: an unfinished transfer waits for the file to be selected
 * again and resumes from the parts that landed.
 */
/** What a file is by its name: a slide, a flat picture, or a document (a PDF, which is only ever a document). */
export type FileKind = 'wsi' | 'flat' | 'document';

/**
 * The one source of truth for a file's TRANSFER. `error` carries the text for
 * `failed`; everything else (done, percentage, "is it moving") is derived.
 * What happens after the bytes are in is the server's `stage`.
 */
export type UploadPhase = 'queued' | 'starting' | 'uploading' | 'finishing' | 'done' | 'failed' | 'waiting-file';

/** The server's refusal of a save, and the field it named when it named one. */
export interface FieldError {
  field: string | null;
  message: string;
}

export interface UploadFile {
  id: string;
  /** Sent with the image's create, so a retried create finds the image the first attempt made. */
  requestKey: string;
  /** The draft image (`img_…`) the bytes and details go to; null until the server has made it. */
  imageId: string | null;
  /** When imageId became known (ms): a poll asked before then cannot say the image is gone. */
  imageAt: number;
  /**
   * The bytes: the file, or a slide's tar (tar.ts). Null for an image the
   * server already has (found on the study, or cut by a reload): an unfinished
   * one waits for the person to select the file again, and the transfer resumes.
   */
  file: Blob | null;
  name: string;
  kind: FileKind;
  /** The Type the file's header is certain of — an X-ray is Radiography (dicomSlides isXray) — or absent: any its kind fits. */
  certainType?: ImageType | null;
  size: number;
  /** File.lastModified, so a re-selected file is the same bytes and not a namesake. 0 when unknown. */
  lastModified: number;
  /**
   * A slide of several DICOM files, sent as one tar (tar.ts): its files, so
   * the same ones are known when they are selected again. `[]` for one the
   * server lists as packed whose files this tab never saw.
   */
  members?: TarMember[];
  /** Bytes the server has, plus bytes in flight. */
  loaded: number;
  phase: UploadPhase;
  error: string | null;
  /**
   * The server refused the upload for want of storage (uploads.ts): in the
   * person's own organization ('yours', their Usage page frees it) or in the
   * workspace owner's ('theirs'). Retry once there is room.
   */
  storageFull?: 'yours' | 'theirs' | null;
  uploadId: string | null;
  /** The part plan the server answered at initiate; kept so a retry resumes. */
  partSize: number;
  partCount: number;
  /** partNumber → ETag of every part the server confirmed. A retry sends only the rest. */
  etags: Record<number, string>;
  /** the per-image fields */
  type: ImageType;
  site: string;
  siteMeaning: string;
  /** Left, Right or Bilateral, said in any order with the site. */
  laterality: '' | Laterality;
  stain: string;
  stainMeaning: string;
  /**
   * The image's Name (its Series Description, what the viewer's image cards
   * show; `description` on the wire). A new file starts with its file's name
   * (suggestedName); the person may type anything over it.
   */
  specDesc: string;
  /**
   * The details as the server last took them, the baseline a save is measured
   * against: only what the person CHANGED is sent, never a field that merely
   * still shows what it showed. Null before the image exists; `{}` when
   * unknown (every field is sent).
   */
  savedDetails: ImageDetails | null;
  /** Details changed here that the server has not taken yet. */
  dirty: boolean;
  /** Why the server refused the last details save (identifier-like text, a bad value); null once it takes them. */
  detailsError: FieldError | null;
  /** Where the server has the image; null before it has said. */
  stage: ImageStage | null;
  /** 0 to 100 while the server converts or indexes it. */
  percent: number | null;
  /** The server's reason for a failed image. */
  stageError: string | null;
}

export const isDone = (f: Pick<UploadFile, 'phase'>) => f.phase === 'done';
/** A transfer that cannot go on until the person selects the file again (a reload dropped the bytes). */
export const needsFile = (f: Pick<UploadFile, 'phase'>) => f.phase === 'waiting-file';
export const RESELECT_MESSAGE = 'The page was reloaded before this file finished. Select it again to continue where it left off.';
export const RESELECT_SLIDE_MESSAGE = 'The page was reloaded before this slide finished. Select its folder (or all of its files) again to continue where it left off.';
/** What a row waiting for its bytes says: a slide of several files is selected again as all of them. */
export const reselectMessage = (f: Pick<UploadFile, 'members'>) => (f.members ? RESELECT_SLIDE_MESSAGE : RESELECT_MESSAGE);
export const isMoving = (f: Pick<UploadFile, 'phase'>) => f.phase === 'queued' || f.phase === 'starting' || f.phase === 'uploading' || f.phase === 'finishing';
/** The stages that mean the image's bytes are on the server. */
const BYTES_IN: ReadonlySet<ImageStage> = new Set(['converting', 'ingesting', 'needs-details', 'published', 'failed']);
/** Nothing more will happen to it by itself: shown to everyone, or failed. */
export const isSettled = (f: Pick<UploadFile, 'phase' | 'stage'>) => f.stage === 'published' || (f.phase === 'done' && f.stage === 'failed');
/**
 * What the uploader must still provide. Only the anatomic site: the Type has
 * a default here (a slide is a slide, a photo is gross), and the server
 * derives one when nobody says (0075's readiness is modality AND site,
 * whoever wrote them; the modality follows the Type). A document needs no
 * site (0099): a requisition or a report is the whole case's.
 */
export const missingDetails = (f: Pick<UploadFile, 'site' | 'stage' | 'kind'>) =>
  !f.site && f.kind !== 'document' && f.stage !== 'published' && f.stage !== 'failed';

/** A file's own percentage: an integer, 100 only once it is done. */
export function filePct(f: Pick<UploadFile, 'phase' | 'loaded' | 'size'>): number {
  switch (f.phase) {
    case 'done':
      return 100;
    case 'finishing':
      return 99;
    case 'uploading':
    case 'failed':
    case 'waiting-file':
      return f.size > 0 ? Math.min(99, Math.floor((Math.min(f.loaded, f.size) / f.size) * 100)) : 0;
    default:
      return 0;
  }
}

export interface UploadSession {
  /** Identity for the bookkeeping that outlives a page (a transfer's tail, the poll). */
  id: string;
  /** The account the session belongs to, once known; a restored session for another account is dropped. */
  owner: string | null;
  colId: string;
  grpId: string | null;
  /** launched from an existing study: step 1 is skipped */
  fromStudy: boolean;
  mode: 'new' | 'existing';
  /** The existing study the images go to (picked on step 1, or the one the upload started from), by public id. */
  studyId: string | null;
  /**
   * The study step 1 made for "Create a new study". Apart from studyId so Back
   * and Continue edit it rather than make a second one, and switching the mode
   * back and forth does not lose it.
   */
  newStudyId: string | null;
  /** Sent with that study's create, so a retried Continue finds the study the first attempt made. */
  studyRequestKey: string;
  /** The collection or group name the upload is filed under; the pages set it. */
  locationName: string;
  label: string;
  sex: string;
  age: string;
  diagnosis: string;
  desc: string;
  search: string;
  files: UploadFile[];
  fileId: string | null;
  dragging: boolean;
}

/** The study a session's images go to: the one it made, or the one it picked. Null until step 1 has one. */
export function targetStudy(s: Pick<UploadSession, 'mode' | 'studyId' | 'newStudyId'>): string | null {
  return s.mode === 'new' ? s.newStudyId : s.studyId;
}

export interface UploadProgress {
  /** Bytes-weighted, 0 to 100. Says 100 only once every file is done. */
  pct: number;
  uploading: number;
  finishing: number;
  /** Cut by a reload; the person has to select the file again. */
  waiting: number;
  failed: number;
  done: number;
  /** Every file is done (failed or waiting ones excluded); false with no files. */
  allDone: boolean;
}

/**
 * The upload as a whole, weighted by bytes. The old figure averaged each
 * file's percentage: nine small finished files and one large one at 96%
 * read "100% uploaded" while the large one was still going, and a file
 * whose last part was being confirmed sat at 99.5 and rounded up to 100.
 */
export function uploadProgress(files: ReadonlyArray<Pick<UploadFile, 'size' | 'loaded' | 'phase'>>): UploadProgress {
  let loaded = 0;
  let total = 0;
  let uploading = 0;
  let finishing = 0;
  let waiting = 0;
  let failed = 0;
  let done = 0;
  for (const f of files) {
    total += f.size;
    loaded += f.phase === 'done' ? f.size : Math.min(f.size, Math.max(0, f.loaded));
    if (f.phase === 'failed') failed++;
    else if (f.phase === 'waiting-file') waiting++;
    else if (f.phase === 'done') done++;
    else if (f.phase === 'finishing') finishing++;
    else uploading++;
  }
  const allDone = files.length > 0 && uploading === 0 && finishing === 0 && waiting === 0 && failed === 0;
  let pct = total > 0 ? Math.floor((loaded / total) * 100) : 0;
  if (!allDone && pct >= 100) pct = 99;
  if (allDone) pct = 100;
  return { pct, uploading, finishing, waiting, failed, done, allDone };
}

/**
 * One image on the progress page, from the session (with its transfer) or
 * from the server alone. The page names no pipeline stage: one word and a
 * percentage, the same figure the admin panel shows (decided 2026-09-10).
 *
 * Every screen but the admin panel says an import is doing one of two
 * things, and only these words (decided 2026-09-23): Uploading (the bytes
 * are on their way, queued and finishing included) and Importing (the
 * server has them: converting, indexing, publishing). It ends Imported, or
 * Could not be imported; what it waits for from the person (Needs details,
 * Select the file again, Upload failed) is named as that.
 */
export type ImportState = 'uploading' | 'select' | 'upload-failed' | 'importing' | 'needs-details' | 'published' | 'failed';
export interface ImportRow {
  id: string;
  name: string;
  state: ImportState;
  percent: number;
  error: string | null;
  /** A slide of several files (tar.ts): selected again as all of them. */
  several?: boolean;
}

function rowOfStage(id: string, name: string, stage: ImageStage | null, percent: number | null, error: string | null): ImportRow {
  switch (stage) {
    case 'published':
      return { id, name, state: 'published', percent: 100, error: null };
    case 'failed':
      return { id, name, state: 'failed', percent: 100, error: error || 'The file could not be imported.' };
    case 'needs-details':
      return { id, name, state: 'needs-details', percent: 100, error: null };
    default:
      return { id, name, state: 'importing', percent: Math.max(0, Math.min(99, percent ?? 0)), error: null };
  }
}

export function importRowOf(f: UploadFile): ImportRow {
  if (f.phase === 'waiting-file') return { id: f.id, name: f.name, state: 'select', percent: 0, error: f.error ?? reselectMessage(f), ...(f.members ? { several: true } : {}) };
  if (f.phase === 'failed') return { id: f.id, name: f.name, state: 'upload-failed', percent: 0, error: f.error };
  if (f.phase !== 'done') return { id: f.id, name: f.name, state: 'uploading', percent: filePct(f), error: null };
  return rowOfStage(f.id, f.name, f.stage, f.percent, f.stageError);
}

/** The same row with no session to know the transfer: an image still without its bytes waits for its file. */
export function importRowOfImage(v: Pick<DraftImage, 'image_id' | 'name' | 'stage' | 'percent' | 'error' | 'packed'>): ImportRow {
  const name = v.name ?? v.image_id;
  if (v.stage === 'waiting-file') {
    return v.packed
      ? { id: v.image_id, name, state: 'select', percent: 0, error: 'Its files were not uploaded. Open the upload and select them (or their folder).', several: true }
      : { id: v.image_id, name, state: 'select', percent: 0, error: 'Its file was not uploaded. Open the upload and select it.' };
  }
  if (v.stage === 'uploading') return { id: v.image_id, name, state: 'uploading', percent: 0, error: null };
  return rowOfStage(v.image_id, name, v.stage, v.percent ?? null, v.error);
}

/**
 * The progress bar: the mean of the rows, a published, failed or
 * details-waiting row counting as complete (its processing is), and a row
 * still uploading as not started.
 */
export function importProgress(rows: ReadonlyArray<Pick<ImportRow, 'state' | 'percent'>>): number {
  if (!rows.length) return 0;
  const sum = rows.reduce((a, r) => a + (r.state === 'uploading' || r.state === 'select' || r.state === 'upload-failed' ? 0 : r.percent), 0);
  return Math.round(sum / rows.length);
}

/** Every row is published or failed: nothing more happens by itself. */
export const importDone = (rows: ReadonlyArray<Pick<ImportRow, 'state'>>) => rows.length > 0 && rows.every((r) => r.state === 'published' || r.state === 'failed');

/**
 * What a session is doing, for every surface that has to agree on it:
 * the top-bar pill, the leave-page prompt, and the "leave?" guard.
 *   none         nothing in it
 *   transferring bytes are moving (or queued to move)
 *   waiting      a transfer needs the person: it failed, or its file has to be selected again
 *   details      the bytes are in; an image still needs its anatomic site
 *   importing    nothing is asked of the person; the server is importing (converting, indexing, publishing)
 *   complete     every image is published or failed
 */
export type UploadWork = 'none' | 'transferring' | 'waiting' | 'details' | 'importing' | 'complete';

export function uploadWork(s: Pick<UploadSession, 'files'> | null): UploadWork {
  if (!s || s.files.length === 0) return 'none';
  if (s.files.some(isMoving)) return 'transferring';
  if (s.files.some((f) => f.phase === 'failed' || needsFile(f))) return 'waiting';
  if (s.files.some((f) => missingDetails(f) || f.detailsError)) return 'details';
  return s.files.every(isSettled) ? 'complete' : 'importing';
}

/**
 * The session as it is saved: everything but the File objects. On load, a
 * finished transfer stays done, an unfinished one waits for its file to be
 * selected again and resumes from its confirmed parts, and the server is asked
 * where every image is.
 */
type SavedFile = Omit<UploadFile, 'file'>;
export interface SavedSession {
  session: Omit<UploadSession, 'files'> & { files: SavedFile[] };
}

export function serializeSession(session: UploadSession): SavedSession {
  return { session: { ...session, files: session.files.map(({ file: _file, ...rest }) => rest) } };
}

/** Every session, as the tab keeps them. */
export interface SavedSessions {
  sessions: Array<SavedSession['session']>;
}

export function serializeSessions(sessions: readonly UploadSession[]): SavedSessions {
  return { sessions: sessions.map((x) => serializeSession(x).session) };
}

/**
 * The sessions as they come back after a reload. A session every image of
 * which is settled has nothing left to do here and is not brought back (the
 * top bar has it from the server until it is dismissed). A tab saved before
 * there could be several holds one (`{ session }`).
 */
export function restoreSessions(saved: unknown): UploadSession[] {
  const raw = saved as Partial<SavedSessions & SavedSession> | null;
  const list = Array.isArray(raw?.sessions) ? raw.sessions : raw?.session ? [raw.session] : [];
  return list.flatMap((x) => {
    const s = restoreSession({ session: x });
    return s && uploadWork(s) !== 'complete' ? [s] : [];
  });
}

/**
 * The session as it comes back after a reload; null when there is nothing
 * usable. A file that was transferring waits to be selected again, keeping
 * its verdict (a refused file stays refused). A session saved before drafts
 * (held uploads, no image ids) cannot be continued and is dropped.
 */
export function restoreSession(saved: unknown): UploadSession | null {
  const s = (saved as Partial<SavedSession> | null)?.session;
  if (!s || typeof s.colId !== 'string' || typeof s.id !== 'string' || typeof s.mode !== 'string' || !Array.isArray(s.files) || !('newStudyId' in s)) return null;
  const files: UploadFile[] = s.files
    .filter((f) => !!f && typeof f.name === 'string' && typeof f.id === 'string' && 'imageId' in f)
    // Saved while the magnification was a field: it is no longer asked for (ingest reads the objective power from the file), and the row forgets it.
    .map(({ magnification: _magnification, ...f }: SavedFile & { magnification?: unknown }) => f)
    .map((f) => ({ ...f, requestKey:f.requestKey ?? requestKeyNew(), savedDetails: f.savedDetails ?? (f.imageId ? {} : null) }))
    // Saved before documents (0099): a PDF was a flat picture typed Gross, which the server now refuses for one.
    .map((f) => (!f.members && kindOf(f.name) === 'document' && f.kind !== 'document' ? { ...f, kind: 'document' as const, type: 'document' as const } : f))
    // Saved before the Type and laterality fields: the defaults, so the first save after the upgrade never clears a published image's Type.
    .map((f) => ({ ...f, type: isImageType(f.type) ? f.type : defaultImageType(f.kind === 'flat' || f.kind === 'document' ? f.kind : 'wsi'), laterality: isLaterality(f.laterality) ? f.laterality : ('' as const) }))
    .map((f) => (f.phase === 'done' ? { ...f, file: null, loaded: f.size } : { ...f, file: null, phase: 'waiting-file' as const }));
  return { ...s, studyId: s.studyId ?? null, studyRequestKey: s.studyRequestKey ?? requestKeyNew(), files, dragging: false };
}

function loadSessions(): UploadSession[] {
  try {
    const raw = window.sessionStorage.getItem(UPLOAD_SESSION_KEY);
    return raw ? restoreSessions(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}
function saveSessions(sessions: readonly UploadSession[]): void {
  if (uploadStorageCleared()) return; // sign-out: nothing may be written back before the reload
  try {
    if (!sessions.length) window.sessionStorage.removeItem(UPLOAD_SESSION_KEY);
    else window.sessionStorage.setItem(UPLOAD_SESSION_KEY, JSON.stringify(serializeSessions(sessions)));
  } catch {
    /* storage unavailable: the sessions live until the tab reloads */
  }
}

export interface StartTarget {
  colId: string;
  grpId: string | null;
  /** An existing study to add to, by public id: step 1 is skipped. */
  studyId?: string | null;
  locationName?: string;
}

interface State {
  /** Oldest first: the order their files queue in. */
  sessions: UploadSession[];
  /**
   * A new session, and its id. Nothing in flight is touched; sessions with
   * nothing in them (a step 1 left half-filled) and those whose every image
   * is settled are let go, since there is nothing left to do in them.
   */
  start: (s: StartTarget) => string;
  patch: (sid: string, p: Partial<UploadSession>) => void;
  /**
   * Step 1's Continue: make the new study, or save its details when this
   * session made it already. Resolves with the refusal, or null.
   */
  saveStudy: (sid: string) => Promise<FieldError | null>;
  /** Files, and slides of several files (dicomSlides.ts), become rows of the session and start uploading. */
  addFiles: (sid: string, files: ReadonlyArray<Picked>) => void;
  removeFile: (id: string) => void;
  retry: (id: string) => void;
  /** One field; the laterality is kept when the site is emptied. */
  setFileField: (id: string, k: 'type' | 'site' | 'siteMeaning' | 'laterality' | 'stain' | 'stainMeaning' | 'specDesc', v: string) => void;
  applyToAll: (sid: string) => number;
  /** Save every unsaved detail now (Finish waits for it). */
  flushDetails: () => Promise<void>;
  /**
   * Files selected again after a reload, matched by name to the transfers
   * waiting for them: the same bytes resume from the confirmed parts, a
   * namesake with other bytes starts that row over, and the rest are added.
   * A file the browser kept for an image (fileHandles.ts) names that image,
   * and goes to its row alone, never to a namesake.
   */
  attachFiles: (sid: string | null, files: ReadonlyArray<Picked | { picked: Picked; imageId: string }>) => AttachResult;
  /**
   * Files waiting to be selected again whose handles the browser kept, opened
   * again. Without `ask` (on its own, wherever the person is): only those it
   * may still read, and only transfers that were cut off — one that ended in a
   * failure waits for the person. With `ask` (a click): the rest, the browser
   * asking to allow it.
   */
  reopenKept: (ask: boolean) => Promise<AttachResult>;
  /** How many kept files wait for that click. */
  reopenable: number;
  /**
   * Square the session with the server's list of its study's images: stages,
   * a complete that landed before a reload, images the server removed. `adopt`
   * also takes the study's unfinished drafts the session does not have (an
   * earlier visit's), so they can be finished here.
   */
  sync: (studyId: string, images: ReadonlyArray<DraftImage>, opts: { askedAt: number; adopt?: boolean }) => void;
  /** Cancel one session (leave). */
  cancel: (sid: string) => void;
  /**
   * Send again the files the server refused for want of storage, those that
   * fit now: the server says how much room there is (GET …/room), and they go
   * in the order they were added while they fit. Called whenever room may have
   * been made (an import finished or failed, an upload was withdrawn, images
   * were deleted) and about once a minute while any wait, from a visible tab.
   */
  retryRefused: () => Promise<void>;
  /** Let go of the sessions of a study whose every image is settled: it was dismissed from the top bar. */
  dismiss: (studyId: string) => void;
  /**
   * Stop everything and forget every session, calling nothing on the server:
   * they belong to another account (UploadShell), whose uploads this one may
   * not touch.
   */
  dropAll: () => void;
}

export interface AttachResult {
  resumed: number;
  replaced: number;
  added: number;
}

const WSI_EXT = new Set(['svs', 'ndpi', 'tif', 'tiff', 'scn', 'dcm', 'zip', 'tar', 'mrxs']);
/** Tries per part; with the backoff cap that is a few minutes of a dead link,
 *  and an offline stretch costs none of them. */
const PART_ATTEMPTS = 12;
/** Tries for the small control calls (initiate, part URLs, complete, details). */
const CALL_ATTEMPTS = 8;

/** A part whose bytes stop moving for this long is dropped and sent again. */
const STALL_MS = 60_000;
/** Once every byte is handed to the socket, how long to wait for the answer. */
const FINAL_MS = 5 * 60_000;
/** A presigned URL that answers 403 is minted again this many times before the 403 is believed. */
const URL_REFRESHES = 2;
/** Progress writes to the store at most this often per file. */
const PROGRESS_EVERY_MS = 150;
/** How often a transferring file tells the server it is alive, when the server does not say. */
const HEARTBEAT_FALLBACK_MS = 45_000;
/** How long a 409 for one's own abandoned upload is waited out (the server names the wait). */
const TAKEOVER_WAIT_CAP_MS = 5 * 60_000;
/** Typing settles for this long before the details go to the server. */
const SAVE_DEBOUNCE_MS = 600;
/** How often the session asks where its images are, while the server is working on them. */
export const POLL_MS = 3000;
const URL_WINDOW = 8;
const STUDY_ID_MAX = 16;

/**
 * ONE pool of in-flight parts for every file together. The server's
 * partConcurrency (4 by default) was applied per file, and a person who drops
 * ten slides opened forty PUTs against a browser that allows six — the rest
 * queued, timed out, and reported "network error".
 */
const partGate = new Semaphore(4);
/** Each file's place in line for part permits: the order its transfer first started, so files finish in the order they were added. */
const rankOf = new Map<string, number>();
let nextRank = 0;
/** id → the controller that stops every request of that file, and the transfer (resolving with its image id). */
const running = new Map<string, { ctl: AbortController; done: Promise<string | null> }>();
/** Images this tab removed: a list fetched before the delete landed must not bring them back. */
const withdrawn = new Set<string>();

/** @internal Test hook: whether a transfer is still going for this file. */
export function isTransferring(id: string): boolean {
  return running.has(id);
}

/** Where the "View" of a finished upload goes; the shell hands in the router's navigate (UploadShell). */
let go: (path: string) => void = (path) => window.location.assign(`${import.meta.env.BASE_URL.replace(/\/+$/, '')}${path}`);
export function setUploadNavigator(fn: (path: string) => void): void {
  go = fn;
}

/** The file's own verdict (a web page saved as .dcm): no retry can change it. */
export class RefusedError extends Error {}

/**
 * Why a file named .dcm is not a DICOM file, or null. What "Save link as" on
 * a GitHub file page gives you is the page itself: it went all the way to the
 * converter and failed there. Only the unmistakable case is refused (no DICM
 * marker and markup at the start); a bare dataset without the Part 10 header
 * is still sent, and the converter decides.
 */
export function notDicomReasonFor(name: string, head: Uint8Array): string | null {
  if (!name.toLowerCase().endsWith('.dcm')) return null;
  const dicm = head.length >= 132 && String.fromCharCode(...head.subarray(128, 132)) === 'DICM';
  if (dicm) return null;
  const first = [...head].find((b) => b !== 0x20 && b !== 0x09 && b !== 0x0a && b !== 0x0d && b !== 0xef && b !== 0xbb && b !== 0xbf);
  if (first !== 0x3c) return null; // '<'
  return `${name} is not a DICOM file: it is a web page saved with a .dcm name. Download the file itself (on GitHub, use Download raw file) and upload that.`;
}

/** notDicomReasonFor over the file itself; reads nothing for other names. */
async function notDicomReason(name: string, file: Blob): Promise<string | null> {
  if (!name.toLowerCase().endsWith('.dcm')) return null;
  return notDicomReasonFor(name, await readHead(file, 132));
}

/** The first `n` bytes of a file. Blob.arrayBuffer is missing in some runtimes (jsdom); FileReader is everywhere. */
async function readHead(file: Blob, n: number): Promise<Uint8Array> {
  return new Uint8Array(await bytesOf(file.slice(0, n)));
}

export function kindOf(name: string): FileKind {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (ext === 'pdf') return 'document';
  return WSI_EXT.has(ext) ? 'wsi' : 'flat';
}

/**
 * What a row's file certainly is, or null until the converter says
 * (data/imageTypes.ts certainKind): a scanner's format is a slide, and so is
 * a MIRAX slide the page packed; a packed slide of DICOM files is not told
 * apart from a flat DICOM picture here.
 */
export const certainKindOf = (f: Pick<UploadFile, 'name' | 'members'>): ImageKind | null => (f.members ? (holdsOf(f.members) === 'mrxs' ? 'wsi' : null) : certainKind(f.name));

/**
 * What a draft's file certainly is, by its name alone: how the conversion
 * stored it (a pyramid, or not) never limits its Type.
 */
export const certainKindOfDraft = (v: Pick<DraftImage, 'image_id' | 'name'>): ImageKind | null => certainKind(v.name ?? v.image_id);

export function fmtSize(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`;
  return `${Math.max(1, Math.round(bytes / 1e3))} KB`;
}

/** The wand: PREFIX-NNN, the prototype's rule. */
export function generateStudyId(collectionTitle: string, existingNames: string[]): string {
  const used = new Set(existingNames.map((n) => n.toLowerCase()));
  const first = existingNames[0] ?? '';
  const m = /^[A-Za-z]+/.exec(first);
  const prefix = (m ? m[0] : collectionTitle.split(/\s+/).map((w) => w[0]).join('').slice(0, 4).toUpperCase()).slice(0, STUDY_ID_MAX - 4);
  let n = existingNames.length + 1;
  while (used.has(`${prefix}-${String(n).padStart(3, '0')}`.toLowerCase())) n++;
  return `${prefix}-${String(n).padStart(3, '0')}`;
}

/** The image fields the server takes and gives back. */
export type ImageFieldsOf = Pick<UploadFile, 'type' | 'site' | 'siteMeaning' | 'laterality' | 'stain' | 'stainMeaning' | 'specDesc'>;

/**
 * An image's details as the server takes them, from the fields: the mapping
 * the metadata sidecar used (convert/.../parsers/simple_json.py), plus the
 * Type (the server writes the modality from it) and the laterality letter.
 * The objective power is not asked for: ingest reads it from the file.
 */
export function imageDetails(f: ImageFieldsOf): Required<Pick<ImageDetails, 'image_type' | 'site' | 'laterality' | 'stain' | 'description'>> {
  return {
    image_type: f.type || null,
    site: f.site ? { code: f.site, scheme: SITE_SCHEME, meaning: f.siteMeaning } : null,
    laterality: f.laterality || null,
    stain: f.stain ? { code: f.stain, scheme: STAIN_SCHEME, meaning: f.stainMeaning } : null,
    description: f.specDesc.trim().slice(0, 64) || null,
  };
}

/**
 * The details the server does not have yet: each field whose value differs
 * from what it last took (`saved`). Everything, when nothing is known.
 */
export function detailsDelta(f: Parameters<typeof imageDetails>[0], saved: ImageDetails | null): ImageDetails {
  const now = imageDetails(f);
  if (!saved) return now;
  const was = saved as Record<string, unknown>;
  return Object.fromEntries(Object.entries(now).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(was[k]))) as ImageDetails;
}
const hasDelta = (f: Parameters<typeof imageDetails>[0], saved: ImageDetails | null) => Object.keys(detailsDelta(f, saved)).length > 0;

/**
 * The fields from an image the server has (one this session did not make, or
 * one being edited after it was published). An image without a Type (a server
 * before 0088) shows its kind's default; nothing is sent until it is changed.
 */
export function fieldsOf(v: DraftImage, kind: FileKind): ImageFieldsOf {
  return {
    // A type the file certainly cannot be (an older row's) shows as the kind's default; a file not known yet keeps what was chosen.
    type: isImageType(v.image_type) && typeFits(v.image_type, certainKindOfDraft(v)) ? v.image_type : defaultImageType(kind),
    site: v.site?.code ?? '',
    siteMeaning: v.site?.meaning ?? '',
    laterality: lateralityOfCode(v.laterality?.code) ?? '',
    stain: v.stain?.code ?? '',
    stainMeaning: v.stain?.meaning ?? '',
    specDesc: v.description ?? '',
  };
}

/** A study's details as the server takes them. Anyone 90 or over is "90+" (Safe Harbor; the server caps it too). */
export function studyDetails(s: Pick<UploadSession, 'label' | 'sex' | 'age' | 'diagnosis' | 'desc'>): StudyDetails & { study_id: string } {
  return {
    study_id: s.label.trim().slice(0, STUDY_ID_MAX),
    description: s.desc.trim().slice(0, 64) || null,
    diagnosis: s.diagnosis.trim().slice(0, 64) || null,
    sex: (s.sex || null) as StudyDetails['sex'],
    age: s.age === '' ? null : Number(s.age) >= 90 ? '90+' : Number(s.age),
  };
}

/** The client's early word on identifier-like text; the server's 422 is the one that counts. */
export function phiHint(text: string): string | null {
  const hits = findIdentifierLikeText(text);
  return hits.length ? `${hits[0].why} (“${hits[0].fragment}”)` : null;
}

/** The images' free text a person can type an identifier into (fields handoff §F). The study's is checked on step 1. */
export function phiProblems(s: Pick<UploadSession, 'files'>): string[] {
  return s.files.flatMap((f) => {
    const hint = phiHint(f.specDesc);
    return hint ? [`${f.name} name: ${hint}`] : [];
  });
}

/**
 * The Name a new image starts with: its file's (imageNameOf), or nothing when
 * that looks like an identifier or a date. The person never typed it, so they
 * are not told to remove it; they name the image themselves.
 */
export function suggestedName(name: string, slide = false): string {
  const n = imageNameOf(name, slide);
  return phiHint(n) ? '' : n;
}

/** What the server's refusal of a save says, and which field it is about. */
export function fieldError(err: unknown): FieldError {
  if (err instanceof UploadApiError) {
    const flagged = Array.isArray(err.body?.fields) ? (err.body.fields as Array<{ field?: string; fragment?: string; why?: string }>)[0] : undefined;
    if (err.status === 422 && flagged) {
      const field = flagged.field === 'specimen_short_description' ? 'description' : flagged.field ?? null;
      return { field, message: `Remove ${flagged.why ?? 'what looks like an identifier'}${flagged.fragment ? ` (“${flagged.fragment}”)` : ''}.` };
    }
    const invalid = /^invalid (\w+)$/.exec(err.message);
    if (err.status === 400 && invalid) return { field: invalid[1] === 'image_type' ? 'type' : invalid[1], message: invalid[1] === 'image_type' ? 'This image cannot be of that type.' : `The server did not accept this ${invalid[1].replace(/_/g, ' ')}.` };
    if (err.status === 400 && /study_id/.test(err.message)) return { field: 'study_id', message: 'A study ID is required.' };
    if (err.status === 404) return { field: null, message: 'The server no longer has this. It may have been removed.' };
    if (err.status === 0 || isTransient(err)) return { field: null, message: 'Could not reach the server. Try again.' };
    return { field: null, message: err.message };
  }
  if (isTransient(err)) return { field: null, message: 'Could not reach the server. Try again.' };
  return { field: null, message: err instanceof Error ? err.message : String(err) };
}

/**
 * Where a session is shown: its files page, named by its study; step 1,
 * named by the session, until it has a study.
 */
export function sessionRoute(base: string, s: Pick<UploadSession, 'id' | 'colId' | 'grpId' | 'mode' | 'studyId' | 'newStudyId'>): string {
  const study = targetStudy(s);
  if (!study) return stepOneRoute(base, s);
  const q = new URLSearchParams();
  if (s.grpId) q.set('group', s.grpId);
  q.set('study', study);
  return `${base}/collections/${s.colId}/upload/files?${q}`;
}

/** Step 1 of a session: Back from its files page comes here, to the same session. */
export function stepOneRoute(base: string, s: Pick<UploadSession, 'id' | 'colId' | 'grpId'>): string {
  const q = new URLSearchParams();
  if (s.grpId) q.set('group', s.grpId);
  q.set('upload', s.id);
  return `${base}/collections/${s.colId}/upload?${q}`;
}

export const sessionById = (sessions: readonly UploadSession[], id: string | null | undefined) => (id ? sessions.find((x) => x.id === id) ?? null : null);
/** The session sending images into a study: one per study (openUpload), or one finished there and not yet let go. */
export function sessionForStudy(sessions: readonly UploadSession[], studyId: string | null | undefined): UploadSession | null {
  if (!studyId) return null;
  const all = sessions.filter((x) => targetStudy(x) === studyId);
  return all.find((x) => uploadWork(x) !== 'complete') ?? all[0] ?? null;
}
/** Files the server refused for want of storage that this tab can send again by itself (it has their bytes). */
export const refusedForRoom = (sessions: readonly UploadSession[]) =>
  sessions.flatMap((x) => x.files).filter((f) => f.phase === 'failed' && !!f.storageFull && !!f.file && !!f.imageId);

/** The session a file row belongs to. */
export const sessionOfFile = (sessions: readonly UploadSession[], fileId: string) => sessions.find((x) => x.files.some((f) => f.id === fileId)) ?? null;

/** The progress page of a study's upload. */
export function importRoute(base: string, colId: string, studyId: string): string {
  return `${base}/collections/${colId}/import/${encodeURIComponent(studyId)}`;
}

/**
 * Open the upload for a target: a study already being uploaded into keeps its
 * session (one per study, so two never send the same study's files), anything
 * else starts a new one. Resolves with the session's id.
 */
export function openUpload(target: StartTarget): string {
  const st = useUpload.getState();
  const open = target.studyId ? sessionForStudy(st.sessions, target.studyId) : null;
  if (open && uploadWork(open) !== 'complete') {
    if (target.locationName && !open.locationName) st.patch(open.id, { locationName: target.locationName });
    return open.id;
  }
  return st.start(target);
}

/** What stands between the files page and Finish. Uploads still running do not: they carry on. */
export function finishGate(s: UploadSession) {
  const p = uploadProgress(s.files);
  const missing = s.files.filter(missingDetails);
  const refused = s.files.filter((f) => f.detailsError);
  const problems = phiProblems(s);
  const ready = s.files.length > 0 && !!targetStudy(s) && p.failed === 0 && p.waiting === 0 && missing.length === 0 && refused.length === 0 && problems.length === 0;
  return { missing, missingCount: missing.length, refused, failedCount: p.failed, waitingCount: p.waiting, uploadingCount: p.uploading + p.finishing, problems, ready, pct: p.pct };
}

/**
 * The sessions with bytes still to send, in the order the pool serves them:
 * the first is sending, the rest are queued behind it (a later one only uses
 * a connection the earlier cannot, between its parts).
 */
export function sendingOrder(sessions: readonly UploadSession[]): string[] {
  const rank = (x: UploadSession) => Math.min(...x.files.filter(isMoving).map((f) => rankOf.get(f.id) ?? Number.MAX_SAFE_INTEGER));
  return sessions.filter((x) => x.files.some(isMoving)).sort((a, b) => rank(a) - rank(b)).map((x) => x.id);
}

const uid = () => Math.random().toString(36).slice(2, 10);
/** A create's request key: unguessable enough that two tabs never share one. */
function requestKeyNew(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `${uid()}${uid()}${uid()}`;
}

const IMAGE_GONE = 'The server no longer has this image (unfinished images are removed after 24 hours). Retry uploads it again.';
const STUDY_GONE = 'The study this upload was going into no longer exists. It was deleted while this page was open; start again from the collection.';

/** The server's refusal for want of storage, and whose storage it is. */
function storageRefusal(err: unknown): 'yours' | 'theirs' | null {
  if (!(err instanceof UploadApiError) || err.status !== 409 || err.reason !== 'storage-full') return null;
  return err.body?.yours === true ? 'yours' : 'theirs';
}

/** What the person reads when a transfer gives up. */
function describeFailure(err: unknown): string {
  const full = storageRefusal(err);
  if (full && err instanceof UploadApiError) {
    const need = Number(err.body?.neededBytes);
    const free = Number(err.body?.freeBytes);
    const sizes = Number.isFinite(need) && Number.isFinite(free) ? ` This file needs ${fmtSize(need)} and ${free > 0 ? `${fmtSize(free)} is free` : 'nothing is free'}.` : '';
    return full === 'yours'
      ? `Not enough storage.${sizes} Free up space on the Usage page, then retry.`
      : `This workspace is out of storage.${sizes} Ask its owner to free up space, then retry.`;
  }
  if (err instanceof UploadApiError) {
    if (err.status === 401) return 'Your session ended. Sign in again, then retry the upload.';
    if (err.status === 403) return 'The storage refused the upload (403). Check the deployment’s bucket permissions, then retry.';
    // An upload the server dropped ("unknown upload"), or a presigned part PUT
    // that 404s with no JSON at all ("part PUT failed (404)"): S3 losing the
    // multipart, which is exactly what Retry starts over from. The image or
    // study being gone is told apart where the call that learns it is made.
    if (err.status === 404) return 'The server no longer has this upload. Retry sends the file again.';
    if (err.status === 0) return 'Upload interrupted. The connection dropped and did not come back. Retry continues where it left off.';
    return err.message;
  }
  if (isTransient(err)) return 'Upload interrupted. The connection dropped and did not come back. Retry continues where it left off.';
  return err instanceof Error ? err.message : String(err);
}

/** A part PUT that the server itself asks us to send again. */
function partRetryable(err: unknown, refreshesLeft: () => number): boolean {
  if (!(err instanceof UploadApiError)) return false;
  // The local route answers 400 for a body cut mid-stream ("part upload
  // interrupted; retry") and S3 answers 400 RequestTimeout for an idle socket.
  if (err.status === 400) return true;
  // A presigned URL past its lifetime answers 403; a fresh one fixes it. The
  // same 403 on a fresh URL is a permission verdict, not an expiry.
  if (err.status === 403) return refreshesLeft() > 0;
  return false;
}

/**
 * A slide's files, by row: what its tar is made of, for keeping (keepSlide)
 * once its image exists. Beside the state, not in it: File objects are not
 * saved, and a row restored after a reload has none until they are selected.
 */
const slideFiles = new Map<string, { files: File[]; paths?: string[] }>();

/** The bytes a row is given: the file itself, or the tar of a slide's files (whose files are remembered for the row). */
function bytesOfPicked(rowId: string, picked: Picked): Pick<UploadFile, 'file' | 'size' | 'lastModified'> & { members?: TarMember[] } {
  if (!isSlide(picked)) return { file: picked, size: picked.size, lastModified: picked.lastModified };
  const tar = tarOf(picked.files, picked.paths);
  slideFiles.set(rowId, { files: picked.files, paths: picked.paths });
  return { file: tar.blob, size: tar.size, lastModified: Math.max(0, ...picked.files.map((f) => f.lastModified)), members: tar.members };
}

/** A kept image opened again (fileHandles.ts), as attachFiles takes it: its file, or its slide's files. */
function reopenedAs(got: Reopened, imageId: string): Array<{ picked: Picked; imageId: string }> {
  if (got instanceof File) return [{ picked: got, imageId }];
  // The row knows the slide's name and what it holds; the tar's member names come back from the files' kept paths.
  if (Array.isArray(got)) return [{ picked: { name: '', holds: holdsOf(got.map((f) => ({ name: f.name, size: f.size, lastModified: f.lastModified }))), files: got, paths: got.map(pathOf) }, imageId }];
  return [];
}

/** What a picked X-ray starts as: Radiography, the only Type it can be (dicomSlides isXray). */
function xrayFields(file: File): Partial<UploadFile> {
  return isXray(file) ? { type: 'radiography', certainType: 'radiography' } : {};
}

/**
 * A session row with the defaults every new image starts from, its Name its
 * file's. A row for an image the server has already (adoptedRow) says its own.
 */
function newFile(name: string, over: Partial<UploadFile>): UploadFile {
  // A slide of several files is named for the slide, not for a file: its kind is given.
  const kind = over.kind ?? kindOf(name);
  return {
    id: uid(), requestKey: requestKeyNew(), imageId: null, imageAt: 0, file: null, name, kind, size: 0, lastModified: 0, loaded: 0, phase: 'queued', error: null, uploadId: null, partSize: 0, partCount: 0, etags: {},
    type: defaultImageType(kind), site: '', siteMeaning: '', laterality: '', stain: '', stainMeaning: '', specDesc: suggestedName(name, over.kind !== undefined),
    savedDetails: null, dirty: false, detailsError: null, stage: null, percent: null, stageError: null,
    ...over,
  };
}

/** A row for an unfinished image of the study that the session did not make (an earlier visit's). */
function adoptedRow(v: DraftImage): UploadFile {
  const name = v.name ?? v.image_id;
  // A slide packed by the page is named for the slide, not for a file: its kind is the server's word.
  const kind = v.packed ? 'wsi' : kindOf(name);
  const bytesIn = BYTES_IN.has(v.stage);
  const fields = fieldsOf(v, kind);
  return newFile(name, {
    imageId: v.image_id, kind, size: v.size ?? 0, loaded: bytesIn ? v.size ?? 0 : 0, phase: bytesIn ? 'done' : 'waiting-file',
    uploadId: v.upload?.id ?? null, partSize: v.upload?.partSize ?? 0, partCount: v.upload?.partCount ?? 0,
    ...(v.packed ? { members: [] } : {}),
    error: bytesIn ? null : v.packed
      ? 'This slide was added earlier and its files never finished uploading. Select its folder (or all of its files) to send them.'
      : 'This image was added earlier and its file never finished uploading. Select the file to send it.',
    ...fields, savedDetails: imageDetails(fields), stage: v.stage, percent: v.percent ?? null, stageError: v.error,
  });
}

/** A study this session made and never put an image in: remove it rather than leave an empty study in the collection. */
async function discardIfEmpty(studyId: string): Promise<void> {
  try {
    if ((await fetchStudyImages(studyId)).length) return;
    await deleteStudy(studyId);
    void invalidateCatalogue(queryClient);
  } catch {
    /* gone already, or unreachable: an empty study is harmless */
  }
}

export const useUpload = create<State>((set, get) => {
  /** Change one session; nothing when it is gone. */
  const patchSession = (sid: string, fn: (x: UploadSession) => UploadSession) =>
    set((st) => (st.sessions.some((x) => x.id === sid) ? { sessions: st.sessions.map((x) => (x.id === sid ? fn(x) : x)) } : {}));
  const patchFile = (id: string, p: Partial<UploadFile>) =>
    set((st) => {
      const owner = sessionOfFile(st.sessions, id);
      return owner ? { sessions: st.sessions.map((x) => (x === owner ? { ...x, files: x.files.map((f) => (f.id === id ? { ...f, ...p } : f)) } : x)) } : {};
    });
  const fileNow = (id: string) => sessionOfFile(get().sessions, id)?.files.find((f) => f.id === id) ?? null;

  // ── details ───────────────────────────────────────────────────────────────
  // Saved as they are typed, per image, one save at a time (a slower earlier
  // answer must not overwrite a later one's stage). The row stays `dirty`
  // until the server has taken exactly what the fields say.
  const saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const saving = new Map<string, Promise<void>>();
  const clearSave = (id: string) => {
    clearTimeout(saveTimers.get(id));
    saveTimers.delete(id);
  };
  const saveNow = (id: string): Promise<void> => {
    clearSave(id);
    const next = (saving.get(id) ?? Promise.resolve()).then(async () => {
      const f = fileNow(id);
      // No image yet: its create carries the details, and marks what it missed.
      if (!f || !f.imageId || !f.dirty) return;
      const body = detailsDelta(f, f.savedDetails);
      if (!Object.keys(body).length) {
        patchFile(id, { dirty: false, detailsError: null });
        return;
      }
      try {
        const v = await withRetry(() => patchDraftImage(f.imageId!, body), { attempts: CALL_ATTEMPTS });
        const now = fileNow(id);
        if (!now || now.imageId !== f.imageId) return;
        const saved = { ...(f.savedDetails ?? {}), ...body };
        patchFile(id, { savedDetails: saved, dirty: hasDelta(now, saved), detailsError: null, stage: v.stage, percent: v.percent ?? null, stageError: v.error });
        if (v.stage === 'published' && now.stage !== 'published') {
          const s = sessionOfFile(get().sessions, id);
          void refreshPublished(queryClient, s ? targetStudy(s) : null);
        }
      } catch (err) {
        // Kept here and still dirty: the next change sends them again.
        if (fileNow(id)?.imageId === f.imageId) patchFile(id, { detailsError: fieldError(err) });
      }
    });
    saving.set(id, next);
    void next.finally(() => {
      if (saving.get(id) === next) saving.delete(id);
    });
    return next;
  };
  const scheduleSave = (id: string) => {
    clearSave(id);
    saveTimers.set(id, setTimeout(() => void saveNow(id), SAVE_DEBOUNCE_MS));
  };

  // ── the transfer ──────────────────────────────────────────────────────────

  /**
   * Make the file's image when it has none, then upload its bytes, resuming
   * when it has an upload id and confirmed parts. Every request is retried
   * through the transfer helpers; a stop comes only from removeFile / cancel
   * (the controller) or from the server's verdict. Resolves with the image
   * id it knows, for a Remove that stopped it before the row heard of it.
   */
  const transfer = async (id: string, ctl: AbortController): Promise<string | null> => {
    const rank = rankOf.get(id) ?? 0;
    const s = sessionOfFile(get().sessions, id);
    const f = fileNow(id);
    if (!s || !f) return null;
    const file = f.file;
    const studyId = targetStudy(s);
    if (!file || !studyId) return f.imageId; // waits for the person to select it again
    const signal = ctl.signal;
    const retryCall = <T>(fn: (sig?: AbortSignal) => Promise<T>) => withRetry((_a, sig) => fn(sig), { attempts: CALL_ATTEMPTS, signal });

    let imageId = f.imageId;
    let imageGone = false;
    let studyGone = false;
    let uploadId = f.uploadId;
    let partSize = f.partSize;
    let partCount = f.partCount;
    let heartbeatMs = HEARTBEAT_FALLBACK_MS;
    let landed = false; // the bytes are in already (a complete before a reload, or another tab's)
    let resumed = !!(uploadId && partCount > 0);
    const etagByPart = new Map<number, string>(resumed ? Object.entries(f.etags).map(([n, e]) => [Number(n), e]) : []);
    const urlByPart = new Map<number, string>();
    const inFlight = new Map<number, number>();
    const snapshot = () => Object.fromEntries(etagByPart);
    const confirmedBytes = () => [...etagByPart.keys()].reduce((a, n) => a + expectedPartBytes(f.size, partSize, n, partCount), 0);
    let doneBytes = 0;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    let flushedEtags = -1;
    // Progress goes to the store at most every PROGRESS_EVERY_MS: every
    // XHR event patched the session and re-rendered the whole page. The
    // confirmed parts ride along whenever a new one landed, so a reload
    // finds them and the transfer resumes from there.
    const flush = () => {
      flushTimer = undefined;
      if (signal.aborted) return;
      let loaded = doneBytes;
      for (const v of inFlight.values()) loaded += v;
      const patch: Partial<UploadFile> = { loaded: Math.min(f.size, loaded) };
      if (etagByPart.size !== flushedEtags) {
        flushedEtags = etagByPart.size;
        patch.etags = snapshot();
      }
      patchFile(id, patch);
    };
    const progress = (now = false) => {
      if (now) {
        if (flushTimer) clearTimeout(flushTimer);
        flush();
      } else if (!flushTimer) flushTimer = setTimeout(flush, PROGRESS_EVERY_MS);
    };

    /**
     * The image, made with the details the fields hold now, its Name among
     * them — unless that looks like an identifier: it goes through the save,
     * so it is refused at the field rather than refusing the image.
     */
    const ensureImage = async () => {
      if (imageId) return;
      const now = fileNow(id) ?? f;
      const details = imageDetails(now);
      const sent = { ...details, description: phiHint(now.specDesc) ? null : details.description };
      let v: DraftImage;
      try {
        // A slide's files go as one tar, and the image is named for the slide (drafts.ts `packed`, `holds`). Its kind is said only when the name is certain of it (a scanner's format), so the server can check the Type fits; otherwise the converter settles it.
        const certain = certainKindOf(f);
        v = await retryCall((sig) => createDraftImage(studyId, { name: f.name, ...(certain ? { kind: certain } : {}), request_key: now.requestKey, ...(f.members ? { packed: 'tar' as const, holds: holdsOf(f.members) } : {}), ...sent }, sig));
      } catch (err) {
        if (err instanceof UploadApiError && err.status === 404) studyGone = true;
        throw err;
      }
      imageId = v.image_id;
      if (signal.aborted) throw abortError(); // Remove deletes it with the id this resolves
      const after = fileNow(id);
      const differs = !!after && hasDelta(after, sent);
      patchFile(id, { imageId, imageAt: Date.now(), savedDetails: sent, stage: v.stage, percent: v.percent ?? null, stageError: v.error, dirty: differs });
      if (differs) void saveNow(id);
    };

    const startFresh = async () => {
      etagByPart.clear();
      urlByPart.clear();
      doneBytes = 0;
      resumed = false;
      patchFile(id, { phase: 'starting', error: null, loaded: 0, etags: {}, uploadId: null });
      // A web page saved as .dcm: say so now, not after the converter fails on
      // it. A slide's files were read to group them (dicomSlides.ts) already.
      const refused = f.members ? null : await notDicomReason(f.name, file);
      if (refused) throw new RefusedError(refused);
      await ensureImage();
      // "Already uploading" can be this person's own upload from a tab that
      // is gone: the server says so, and how long until it hands the image
      // over. Anyone else's is a verdict at once.
      let init: Awaited<ReturnType<typeof initiateImageUpload>>;
      let waited = 0;
      for (;;) {
        try {
          init = await retryCall((sig) => initiateImageUpload(imageId!, { size: f.size, contentType: file.type || undefined }, sig));
          break;
        } catch (err) {
          if (err instanceof UploadApiError && err.status === 404) imageGone = true;
          // Its bytes are in already: a complete that landed before a reload.
          if (err instanceof UploadApiError && err.status === 409 && err.reason === 'has-file') {
            landed = true;
            return;
          }
          const own = err instanceof UploadApiError && err.status === 409 && err.reason === 'uploading' && err.body?.mine === true;
          const wait = own ? Math.min(60_000, Math.max(1000, Number(err.body?.retryAfterMs) || 5000)) : 0;
          if (!own || waited + wait > TAKEOVER_WAIT_CAP_MS) throw err;
          waited += wait;
          await sleep(wait, signal);
        }
      }
      uploadId = init.uploadId;
      partSize = init.partSize;
      partCount = init.partCount;
      heartbeatMs = init.heartbeatMs ?? HEARTBEAT_FALLBACK_MS;
      partGate.setCapacity(init.partConcurrency);
      init.urls.forEach((u) => urlByPart.set(u.partNumber, u.url));
      // The server now holds an upload; a stop that landed during the call
      // (removeFile, cancel, start) never saw the id, so hand it back here.
      if (signal.aborted) {
        void abortUpload(uploadId);
        throw abortError();
      }
      patchFile(id, { uploadId, partSize, partCount });
    };

    let urlFetch: Promise<void> | null = null;
    const ensureUrl = async (n: number) => {
      for (let tries = 0; !urlByPart.has(n); tries++) {
        // A fetch already in flight may be for another window: wait for it,
        // and if it did not bring this part's URL, ask for this window.
        if (tries > 2) throw new UploadApiError(`no URL for part ${n}`, 0);
        if (!urlFetch) {
          const wanted: number[] = [];
          for (let k = n; k <= partCount && wanted.length < URL_WINDOW; k++) if (!urlByPart.has(k) && !etagByPart.has(k)) wanted.push(k);
          if (!wanted.includes(n)) wanted.unshift(n);
          urlFetch = retryCall((sig) => fetchPartUrls(uploadId!, wanted, sig))
            .then((urls) => urls.forEach((u) => urlByPart.set(u.partNumber, u.url)))
            .finally(() => (urlFetch = null));
        }
        await urlFetch;
      }
      return urlByPart.get(n)!;
    };

    const uploadPart = (n: number, round: AbortSignal) => {
      const blob = file.slice((n - 1) * partSize, Math.min(n * partSize, f.size));
      let refreshes = 0;
      return withRetry(
        async () => {
          // The permit first, then the URL: a file waits its turn behind the
          // files added before it (Semaphore), perhaps for longer than a URL
          // lives. The permit still covers only this attempt: a part sleeping
          // through its backoff holds no connection slot.
          const release = await partGate.acquire(round, rank);
          try {
            const url = await ensureUrl(n);
            const { etag } = await guardStall(
              (sig, progressed) =>
                putPart(url, blob, {
                  signal: sig,
                  onProgress: (l) => {
                    inFlight.set(n, l);
                    progressed(l >= blob.size);
                    progress();
                  },
                }),
              { stallMs: STALL_MS, finalMs: FINAL_MS },
              round,
            );
            etagByPart.set(n, etag);
            urlByPart.delete(n);
            inFlight.delete(n);
            doneBytes += blob.size;
            progress(true);
          } catch (err) {
            inFlight.delete(n);
            if (err instanceof UploadApiError && err.status === 403) {
              urlByPart.delete(n);
              refreshes++;
            }
            throw err;
          } finally {
            release();
          }
        },
        { attempts: PART_ATTEMPTS, signal: round, retryable: (err) => isTransient(err) || partRetryable(err, () => URL_REFRESHES - refreshes + 1) },
      );
    };

    const sendMissing = async () => {
      const queue: number[] = [];
      for (let n = 1; n <= partCount; n++) if (!etagByPart.has(n)) queue.push(n);
      if (!queue.length) return;
      patchFile(id, { phase: 'uploading' });
      // The round's workers share a signal under the file's: when one part
      // gives up, the others are stopped and AWAITED before the error goes
      // up — a restart (new upload id) must not race a worker still sending
      // to the old one.
      const roundCtl = new AbortController();
      const onFileAbort = () => roundCtl.abort();
      signal.addEventListener('abort', onFileAbort, { once: true });
      try {
        // One worker per permit; the gate is what limits the wire.
        const workers = Array.from({ length: Math.min(partGate.capacity, queue.length) }, async () => {
          while (queue.length && !roundCtl.signal.aborted) {
            const n = queue.shift()!;
            await uploadPart(n, roundCtl.signal);
          }
        });
        try {
          await Promise.all(workers);
        } catch (err) {
          roundCtl.abort();
          await Promise.allSettled(workers);
          inFlight.clear();
          throw err;
        }
      } finally {
        signal.removeEventListener('abort', onFileAbort);
      }
    };

    let heartbeat: ReturnType<typeof setInterval> | undefined;
    try {
      if (resumed) {
        patchFile(id, { phase: 'starting', error: null });
        // The server's list of parts is the truth: a part this tab thought
        // confirmed may not have landed, and one it never heard back about may
        // have. Unknown upload (an expiry, a takeover) → fresh.
        const held = await retryCall((sig) => fetchParts(uploadId!, sig));
        if (!held) {
          await startFresh();
        } else if (held.completed) {
          landed = true; // its complete landed before the reload: nothing to send
        } else {
          etagByPart.clear();
          for (const p of held.parts) etagByPart.set(p.partNumber, p.etag);
          partSize = held.partSize;
          partCount = held.partCount;
          patchFile(id, { partSize, partCount, etags: snapshot() });
          doneBytes = confirmedBytes();
        }
      } else {
        await startFresh();
      }
      // Kept where the browser allows, so a tab that closes mid-transfer can open the file (a slide: its files) again.
      if (!landed && imageId) void (f.members ? keepSlide(imageId, slideFiles.get(id)?.files ?? [], slideFiles.get(id)?.paths) : keepFile(imageId, file as File));
      if (imageId) unmarkLeft([imageId]);
      if (!landed) heartbeat = setInterval(() => { if (uploadId) void touchUpload(uploadId, signal).catch(() => undefined); }, heartbeatMs);
      // The local transport can answer "parts missing" (up to 20 named per
      // answer) when a part it acknowledged never landed: send those and
      // complete again, as many rounds as the plan could need.
      const maxRounds = Math.ceil(partCount / 20) + 2;
      for (let round = 0; !landed; round++) {
        try {
          await sendMissing();
          patchFile(id, { phase: 'finishing', loaded: f.size });
          // Named by its image on the server: no file name to send.
          await retryCall((sig) => completeUpload(uploadId!, [...etagByPart].map(([partNumber, etag]) => ({ partNumber, etag })), sig));
          break;
        } catch (err) {
          // Resuming an upload the server no longer has: start over once.
          if (resumed && err instanceof UploadApiError && err.status === 404) {
            await startFresh();
            continue;
          }
          const missing = err instanceof UploadApiError && err.status === 400 ? err.missing ?? [] : [];
          if (!missing.length || round >= maxRounds) throw err;
          missing.forEach((n) => etagByPart.delete(n));
          doneBytes = confirmedBytes();
          patchFile(id, { etags: snapshot() });
        }
      }
      // Stopped while the complete was in flight: Remove deletes the image
      // (with its conversion), leaving keeps it. Either way not this row's.
      if (signal.aborted) throw abortError();
      progress(true);
      const stage = fileNow(id)?.stage;
      // The server queued the conversion with the complete; the poll takes it from here.
      const queued = !stage || stage === 'waiting-file' || stage === 'uploading';
      patchFile(id, { phase: 'done', loaded: f.size, error: null, etags: snapshot(), ...(queued ? { stage: 'converting' as const, percent: 0 } : {}) });
      if (imageId) void forgetFile(imageId);
      void refreshUploadList(queryClient);
    } catch (err) {
      if (flushTimer) clearTimeout(flushTimer);
      // Removed or cancelled: the file is gone from the session, nothing to record.
      if (isAbort(err) || signal.aborted) return imageId;
      // One part's verdict ends the file: stop the sibling workers too, or
      // they keep sending (and holding permits) under a row that says failed.
      ctl.abort();
      const lost = err instanceof UploadApiError && err.status === 404;
      patchFile(id, {
        phase: 'failed',
        error: studyGone ? STUDY_GONE : imageGone ? IMAGE_GONE : describeFailure(err),
        storageFull: storageRefusal(err),
        etags: lost ? {} : snapshot(),
        ...(lost ? { uploadId: null, loaded: 0 } : { loaded: confirmedBytes() }),
        // Retry makes the image again, with the details the fields hold.
        ...(imageGone ? { imageId: null, requestKey: requestKeyNew(), savedDetails: null, stage: null, percent: null, stageError: null, dirty: false, detailsError: null } : {}),
      });
    } finally {
      if (heartbeat) clearInterval(heartbeat);
    }
    return imageGone ? null : imageId;
  };

  const run = (id: string) => {
    if (running.has(id)) return;
    if (!rankOf.has(id)) rankOf.set(id, nextRank++);
    const ctl = new AbortController();
    const entry = { ctl, done: Promise.resolve<string | null>(null) };
    running.set(id, entry);
    entry.done = transfer(id, ctl).finally(() => {
      if (running.get(id) === entry) running.delete(id);
    });
  };

  /**
   * Leave a session (Cancel, its destination deleted, or nothing left to do
   * in it). Its images STAY: those with their bytes and site publish by
   * themselves, and the server removes the unfinished ones after a day — or
   * the study's upload page picks them up again. Open uploads are handed
   * back, details typed a moment ago still go, and a study the session made
   * and never filled is removed. The caller takes it out of the list.
   */
  const leave = (s: UploadSession, onPurpose = true) => {
    // Left on purpose: its unfinished images are not "interrupted" (leftUploads.ts).
    if (onPurpose) markLeft(s.files.filter((f) => f.imageId && f.stage !== 'published').map((f) => f.imageId!));
    const mine = new Set(s.files.map((f) => f.imageId));
    keptToAsk = keptToAsk.filter((k) => !mine.has(k.imageId));
    set({ reopenable: keptToAsk.length });
    void refreshUploadList(queryClient);
    for (const f of s.files) {
      clearSave(f.id);
      slideFiles.delete(f.id);
      running.get(f.id)?.ctl.abort();
      const unsaved = f.dirty && f.imageId ? detailsDelta(f, f.savedDetails) : {};
      if (f.imageId && Object.keys(unsaved).length) void patchDraftImage(f.imageId, unsaved).catch(() => undefined);
      if (f.phase !== 'done' && f.uploadId) void abortUpload(f.uploadId);
    }
    if (s.newStudyId) void discardIfEmpty(s.newStudyId);
  };

  /** Step 1's Continue in flight, per session. */
  const studySaves = new Map<string, Promise<FieldError | null>>();
  /** One room check at a time (retryRefused). */
  let roomCheck: Promise<void> | null = null;
  /** Kept files waiting for the person's click (reopenKept). */
  let keptToAsk: KeptFile[] = [];

  return {
    sessions: loadSessions(),
    start: ({ colId, grpId, studyId, locationName }) => {
      // Nothing in it (step 1 left half-filled), or nothing left to do in it: let it go.
      const idle = get().sessions.filter((x) => x.files.length === 0 || uploadWork(x) === 'complete');
      for (const x of idle) leave(x, false);
      const id = uid();
      set((st) => ({
        sessions: [
          ...st.sessions.filter((x) => !idle.includes(x)),
          {
            id, owner: null, colId, grpId, fromStudy: !!studyId, mode: studyId ? 'existing' : 'new', studyId: studyId ?? null, newStudyId: null, studyRequestKey: requestKeyNew(), locationName: locationName ?? '',
            label: '', sex: '', age: '', diagnosis: '', desc: '', search: '', files: [], fileId: null, dragging: false,
          },
        ],
      }));
      return id;
    },
    patch: (sid, p) => patchSession(sid, (x) => ({ ...x, ...p })),
    saveStudy: (sid) => {
      // One at a time: a double click must not make two studies.
      const inFlight = studySaves.get(sid);
      if (inFlight) return inFlight;
      const save = (async (): Promise<FieldError | null> => {
        const s = sessionById(get().sessions, sid);
        if (!s) return null;
        if (s.mode === 'existing') return s.studyId ? null : { field: null, message: 'Choose the study to add the images to.' };
        const body = studyDetails(s);
        if (!body.study_id) return { field: 'study_id', message: 'A study ID is required.' };
        for (const [field, text] of [['diagnosis', s.diagnosis], ['description', s.desc]] as const) {
          const hint = phiHint(text);
          if (hint) return { field, message: `Remove ${hint}.` };
        }
        try {
          if (s.newStudyId) {
            await withRetry(() => patchStudyDetails(s.newStudyId!, body), { attempts: CALL_ATTEMPTS });
          } else {
            // Retried like any call: its request key makes a repeat find the study the first attempt made.
            const made = await withRetry(() => createDraftStudy({ ...body, collectionId: s.colId, studyGroupId: s.grpId, request_key: s.studyRequestKey }), { attempts: CALL_ATTEMPTS });
            if (!sessionById(get().sessions, sid)) {
              void discardIfEmpty(made.public_id);
              return null;
            }
            patchSession(sid, (x) => ({ ...x, newStudyId: made.public_id }));
          }
          void invalidateCatalogue(queryClient);
          return null;
        } catch (err) {
          return fieldError(err);
        }
      })().finally(() => {
        studySaves.delete(sid);
      });
      studySaves.set(sid, save);
      return save;
    },
    addFiles: (sid, files) => {
      const s = sessionById(get().sessions, sid);
      if (!s || !targetStudy(s)) return;
      // The same file (or slide) dropped twice is one image; a namesake of other size is another.
      const have = new Set(s.files.map((f) => `${f.name}\n${f.size}`));
      const next: UploadFile[] = [];
      for (const picked of files) {
        const row = isSlide(picked) ? newFile(picked.name, { kind: 'wsi' }) : newFile(picked.name, xrayFields(picked));
        const bytes = bytesOfPicked(row.id, picked);
        const key = `${row.name}\n${bytes.size}`;
        if (have.has(key)) {
          slideFiles.delete(row.id);
          continue;
        }
        have.add(key);
        next.push({ ...row, ...bytes });
      }
      if (!next.length) return;
      patchSession(sid, (x) => ({ ...x, files: [...x.files, ...next], fileId: x.fileId ?? next[0].id }));
      next.forEach((f) => run(f.id));
    },
    removeFile: (id) => {
      const s = sessionOfFile(get().sessions, id);
      const f = s?.files.find((x) => x.id === id);
      if (!s || !f) return;
      // A published image belongs to its study now: deleted from the study's page, not here.
      if (f.stage === 'published') return;
      patchSession(s.id, (x) => {
        const files = x.files.filter((y) => y.id !== id);
        return { ...x, files, fileId: x.fileId === id ? files[0]?.id ?? null : x.fileId };
      });
      clearSave(id);
      slideFiles.delete(id);
      const t = running.get(id);
      t?.ctl.abort();
      void (async () => {
        // The transfer settles first: its image may have been made during the stop.
        const imageId = (t ? await t.done.catch(() => null) : null) ?? f.imageId;
        if (!imageId) return;
        withdrawn.add(imageId);
        try {
          await withRetry(() => deleteDraftImage(imageId), { attempts: CALL_ATTEMPTS });
          void forgetFile(imageId);
          void invalidateCatalogue(queryClient);
          void refreshUploadList(queryClient);
          void get().retryRefused();
        } catch (err) {
          // Refused (being indexed, or published meanwhile) or unreachable: it
          // is still there, so its row comes back and says so.
          withdrawn.delete(imageId);
          const message = err instanceof Error ? err.message : String(err);
          toast(`${f.name} could not be removed: ${message}`);
          patchSession(s.id, (x) =>
            x.files.some((y) => y.imageId === imageId)
              ? x
              : { ...x, files: [...x.files, f.phase === 'done' ? { ...f, imageId } : { ...f, imageId, phase: 'failed', error: `Could not be removed: ${message}` }] },
          );
        }
      })();
    },
    retry: (id) => {
      const f = fileNow(id);
      if (!f || !f.file || running.has(id) || f.phase !== 'failed') return;
      patchFile(id, { error: null, storageFull: null, phase: 'queued' });
      run(id);
    },
    attachFiles: (sid, given) => {
      const all = get().sessions;
      const s = sessionById(all, sid);
      const fresh: Picked[] = [];
      let resumed = 0;
      let replaced = 0;
      const taken = new Set<string>();
      for (const g of given) {
        const picked = 'imageId' in g ? g.picked : g;
        const imageId = 'imageId' in g ? g.imageId : null;
        // A kept file names its image, wherever its session is; a file (or slide) picked by hand goes to this session's rows.
        const row = imageId
          ? all.flatMap((x) => x.files).find((f) => !taken.has(f.id) && needsFile(f) && f.imageId === imageId)
          : s?.files.find((f) => !taken.has(f.id) && needsFile(f) && f.name === picked.name);
        if (!row) {
          // A kept file whose image no longer waits (selected by hand meanwhile): nothing to do.
          if (!imageId) fresh.push(picked);
          continue;
        }
        taken.add(row.id);
        const bytes = bytesOfPicked(row.id, picked);
        // A size of 0 is an image found on the server before any bytes: unknown,
        // so taken as these; a slide's files likewise when the row never saw
        // them (members []), else matched as a set (the same tar comes of them).
        const same = bytes.members
          ? row.members !== undefined && (!row.members.length || sameMembers(row.members, bytes.members))
          : !row.members && (row.size === 0 || row.size === bytes.size) && (row.lastModified === 0 || row.lastModified === bytes.lastModified);
        if (same) {
          resumed++;
          patchFile(row.id, { ...bytes, error: null, storageFull: null, phase: 'queued', ...(!isSlide(picked) && isXray(picked) ? { certainType: 'radiography' as const } : {}) });
          run(row.id);
          continue;
        }
        // Same name, other bytes: the upload that was waiting is not this
        // file's. Hand it back and send this one from the start, to the same
        // image (a slide's files, or one file: whichever these are).
        replaced++;
        patchFile(row.id, { members: undefined, ...bytes, loaded: 0, uploadId: null, partSize: 0, partCount: 0, etags: {}, error: null, storageFull: null, phase: 'queued' });
        const old = row.uploadId;
        // The server keeps one live upload per image: the old one goes first,
        // or the fresh initiate answers "already uploading".
        if (old) void abortUpload(old).catch(() => undefined).then(() => run(row.id));
        else run(row.id);
      }
      if (fresh.length && sid) get().addFiles(sid, fresh);
      return { resumed, replaced, added: sid ? fresh.length : 0 };
    },
    reopenable: 0,
    reopenKept: async (ask) => {
      const none = { resumed: 0, replaced: 0, added: 0 };
      let found: Array<{ picked: Picked; imageId: string }> = [];
      if (ask) {
        // Straight into the prompts: nothing awaited first, so the click still counts (reopenFiles).
        const list = keptToAsk;
        const got = await reopenFiles(list, true);
        found = list.flatMap((k, i) => reopenedAs(got[i], k.imageId));
        keptToAsk = list.filter((_, i) => got[i] === 'ask');
      } else {
        const rows = get().sessions.flatMap((x) => x.files).filter((f) => needsFile(f) && f.imageId);
        const kept = await keptFiles(rows.map((f) => f.imageId!));
        // Cut off: opened again on its own. Ended in a failure: waits for the person's click.
        const cutOff = rows.filter((f) => !f.error).flatMap((f) => (kept.has(f.imageId!) ? [kept.get(f.imageId!)!] : []));
        const failed = rows.filter((f) => f.error).flatMap((f) => (kept.has(f.imageId!) ? [kept.get(f.imageId!)!] : []));
        const got = await reopenFiles(cutOff, false);
        found = cutOff.flatMap((k, i) => reopenedAs(got[i], k.imageId));
        keptToAsk = [...cutOff.filter((_, i) => got[i] === 'ask'), ...failed];
      }
      set({ reopenable: keptToAsk.length });
      return found.length ? get().attachFiles(null, found) : none;
    },
    setFileField: (id, k, v) => {
      patchFile(id, { [k]: v, dirty: true });
      scheduleSave(id);
    },
    applyToAll: (sid) => {
      const s = sessionById(get().sessions, sid);
      const src = s?.files.find((x) => x.id === s.fileId);
      if (!s || !src) return 0;
      const others = s.files.filter((f) => f.id !== src.id && f.stage !== 'published');
      // The Type describes how the image was made: it goes to every image but
      // one whose file certainly cannot be that Type (a scanner's slide is no
      // gross photo). A PDF's Type was never chosen (it is always Documents),
      // so it goes nowhere. The laterality goes with the site it qualifies.
      // The Name is each image's own and stays.
      const typeChosen = certainKindOf(src) !== 'document';
      const copy = (f: UploadFile): UploadFile => {
        const fits = typeChosen && typeAllowed(src.type, certainKindOf(f), f.certainType ?? null);
        return { ...f, site: src.site, siteMeaning: src.siteMeaning, laterality: src.laterality, stain: src.stain, stainMeaning: src.stainMeaning, ...(fits ? { type: src.type } : {}), dirty: true };
      };
      patchSession(sid, (x) => ({ ...x, files: x.files.map((f) => (others.includes(f) ? copy(f) : f)) }));
      others.forEach((f) => scheduleSave(f.id));
      return others.length;
    },
    flushDetails: async () => {
      const ids = get().sessions.flatMap((x) => x.files).filter((f) => f.dirty || saving.has(f.id)).map((f) => f.id);
      await Promise.all(ids.map((id) => saveNow(id)));
    },
    sync: (studyId, images, { askedAt, adopt }) => {
      const s = sessionForStudy(get().sessions, studyId);
      if (!s) return;
      const byId = new Map(images.map((v) => [v.image_id, v]));
      let changed = false;
      let gone = 0;
      let published = 0;
      const files: UploadFile[] = [];
      for (const f of s.files) {
        const v = f.imageId ? byId.get(f.imageId) : undefined;
        // Not asked about: no image yet, one made after the list was asked
        // for, or a transfer still running (it finds out by itself).
        if (!f.imageId || (!v && (f.imageAt >= askedAt || running.has(f.id)))) {
          files.push(f);
          continue;
        }
        if (!v) {
          // The server removed it: withdrawn elsewhere, or expired unfinished.
          changed = true;
          gone++;
          clearSave(f.id);
          continue;
        }
        const next: UploadFile = { ...f, stage: v.stage, percent: v.percent ?? null, stageError: v.error };
        if (v.stage === 'published' && f.stage !== 'published') published++;
        // Its bytes are in: the complete landed, perhaps before a reload cut the answer.
        if (BYTES_IN.has(v.stage) && f.phase !== 'done' && !running.has(f.id)) Object.assign(next, { phase: 'done', loaded: f.size || v.size || 0, error: null });
        // The upload to resume, when the row lost it: the server gives it only to its owner.
        if (!next.uploadId && v.upload && next.phase === 'waiting-file') Object.assign(next, { uploadId: v.upload.id, partSize: v.upload.partSize, partCount: v.upload.partCount, etags: {} });
        const differs = next.stage !== f.stage || next.percent !== f.percent || next.stageError !== f.stageError || next.phase !== f.phase || next.uploadId !== f.uploadId;
        if (differs) changed = true;
        files.push(differs ? next : f);
      }
      if (adopt) {
        const known = new Set(files.map((f) => f.imageId));
        // Published ones are done. A live upload is someone else's to finish —
        // unless the server offers it to this person (`upload`): theirs, from a
        // tab that is gone, to resume here once the file is selected again.
        for (const v of images) {
          if (known.has(v.image_id) || withdrawn.has(v.image_id) || v.stage === 'published' || (v.stage === 'uploading' && !v.upload)) continue;
          files.push(adoptedRow(v));
          // Picked up again here: no longer an upload the person left.
          unmarkLeft([v.image_id]);
          changed = true;
        }
      }
      if (!changed) return;
      const before = uploadWork(s);
      const session = { ...s, files, fileId: files.some((f) => f.id === s.fileId) ? s.fileId : files[0]?.id ?? null };
      patchSession(s.id, () => session);
      if (gone) toast(gone === 1 ? 'One unfinished image was removed by the server.' : `${gone} unfinished images were removed by the server.`);
      // An image just became visible: the collection page, Home, Explore, the
      // study header and the study's image list in the viewer show it, so they reload now.
      if (published) void refreshPublished(queryClient, targetStudy(session));
      if (before !== 'complete' && uploadWork(session) === 'complete') {
        announce(session);
        void refreshUploadList(queryClient);
      }
      // An import that finished counts at its stored size, one that failed not
      // at all: either may have made room.
      const was = new Map(s.files.map((f) => [f.id, f.stage]));
      if (published || files.some((f) => f.stage === 'failed' && was.get(f.id) !== 'failed')) void get().retryRefused();
    },
    cancel: (sid) => {
      const s = sessionById(get().sessions, sid);
      if (!s) return;
      leave(s);
      set((st) => ({ sessions: st.sessions.filter((x) => x.id !== sid) }));
      // Its uploads no longer hold room.
      void get().retryRefused();
    },
    retryRefused: () => {
      if (roomCheck) return roomCheck;
      roomCheck = (async () => {
        // Per session: a study's files share a workspace, and so an organization.
        const bySession = new Map<string, UploadFile[]>();
        for (const f of refusedForRoom(get().sessions)) {
          const sid = sessionOfFile(get().sessions, f.id)?.id;
          if (sid) bySession.set(sid, [...(bySession.get(sid) ?? []), f]);
        }
        for (const files of bySession.values()) {
          // In the order they were added; at most 200 asked about at once (the server's cap).
          const inOrder = [...files].sort((a, b) => (rankOf.get(a.id) ?? 0) - (rankOf.get(b.id) ?? 0)).slice(0, 200);
          const fits = await fetchUploadRoom(inOrder[0].imageId!, inOrder.map((f) => Math.max(1, f.size))).catch(() => null);
          if (!fits) continue;
          inOrder.forEach((f, i) => {
            if (fits[i]) get().retry(f.id);
          });
        }
      })().finally(() => {
        roomCheck = null;
      });
      return roomCheck;
    },
    dismiss: (studyId) => {
      const done = get().sessions.filter((x) => targetStudy(x) === studyId && uploadWork(x) === 'complete');
      if (!done.length) return;
      for (const x of done) leave(x, false);
      set((st) => ({ sessions: st.sessions.filter((x) => !done.includes(x)) }));
    },
    dropAll: () => {
      for (const x of get().sessions) {
        for (const f of x.files) {
          clearSave(f.id);
          running.get(f.id)?.ctl.abort();
        }
      }
      keptToAsk = [];
      set({ sessions: [], reopenable: 0 });
    },
  };
});

/** Every image settled, and the person elsewhere: say so, with the way to the study. */
function announce(s: UploadSession): void {
  const studyId = targetStudy(s);
  if (!studyId || window.location.pathname.includes(`/import/${studyId}`)) return;
  const failed = s.files.filter((f) => f.stage === 'failed').length;
  const n = s.files.length - failed;
  toast(
    failed ? `Imported with ${failed} error${failed > 1 ? 's' : ''}` : s.mode === 'existing' ? `${n} image${n === 1 ? '' : 's'} imported` : `Study ${s.label} imported`,
    n ? { label: 'View', fn: () => { dismissUpload(studyId); go(`/studies/${encodeURIComponent(studyId)}`); } } : undefined,
  );
}

/**
 * The person has seen how an upload ended (opened it, or dismissed it): it
 * leaves the top bar, on every device (DELETE /uploads/active/:study), and
 * this tab lets go of its session.
 */
export function dismissUpload(studyId: string): void {
  useUpload.getState().dismiss(studyId);
  queryClient.setQueryData<ActiveUpload[]>(UPLOAD_LIST_KEY, (list) => list?.filter((u) => u.study !== studyId));
  void dismissActiveUpload(studyId).catch(() => undefined);
}

/**
 * Let an upload go when the thing it was being filed into is deleted.
 *
 * The top-bar pill reads the session, so a collection deleted from the
 * Collections page left it claiming work was still in flight, in amber, for
 * an upload with nowhere to land. This is the upload pages' own verdict on a
 * 404 (UploadFilesPage, UploadStudyPage), reached from the page that caused it.
 *
 * ANY field that is passed and matches is enough, so each caller passes the
 * one thing it deleted: a group delete strands only a session filing into THAT
 * group, not every session in the collection. Do not pass two hoping for "this
 * group in this collection" — that would match the collection alone.
 *
 * The study is matched by PUBLIC id — a UID is unique only inside a workspace,
 * so a namesake deleted elsewhere must not take this session with it — and
 * against the study the images go to: the one step 1 made as much as one it
 * picked, since a draft study is listed (and deletable) the moment it exists.
 */
export function forgetUploadInto(t: { colId?: string; grpId?: string; studyId?: string }): void {
  for (const s of useUpload.getState().sessions) {
    const hit =
      (t.colId !== undefined && s.colId === t.colId) ||
      (t.grpId !== undefined && s.grpId === t.grpId) ||
      (t.studyId !== undefined && targetStudy(s) === t.studyId);
    if (hit) useUpload.getState().cancel(s.id);
  }
}

/** Whether the server is still working on something the session is waiting to hear about. */
function needsPolling(s: UploadSession): boolean {
  return !!targetStudy(s) && s.files.some((f) => f.imageId && f.phase === 'done' && f.stage !== 'published' && f.stage !== 'failed' && f.stage !== 'needs-details');
}

/** How often the top bar asks for the list, and a session for its study while its page is not the one open. */
export const SLOW_POLL_MS = 10_000;

/** Whether this study's own page (its files or its progress) is what the person is looking at. */
function watching(studyId: string): boolean {
  if (typeof document === 'undefined' || document.visibilityState === 'hidden') return false;
  const { pathname, search } = window.location;
  return pathname.endsWith(`/import/${encodeURIComponent(studyId)}`) || (pathname.endsWith('/upload/files') && new URLSearchParams(search).get('study') === studyId);
}

// ── the poll ────────────────────────────────────────────────────────────────
// A timer per session, asking where its study's images are until every one
// that has its bytes is published, failed, or waiting for the person. An
// image with its site publishes by itself when indexing ends, so this is how
// the pages, the top bar and the "ready" toast hear of it. Every POLL_MS while
// the study's own page is open, every SLOW_POLL_MS otherwise, and not at all
// from a hidden tab (it asks at once when the tab is shown again).
{
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const busy = new Set<string>();
  const tick = async (sid: string) => {
    timers.delete(sid);
    const s = sessionById(useUpload.getState().sessions, sid);
    const studyId = s && targetStudy(s);
    if (!s || !studyId || !needsPolling(s)) return;
    if (document.visibilityState === 'hidden') return schedule();
    busy.add(sid);
    const askedAt = Date.now();
    try {
      useUpload.getState().sync(studyId, await fetchStudyImages(studyId), { askedAt });
    } catch (err) {
      // The study was deleted elsewhere: the session has nowhere to go.
      if (err instanceof UploadApiError && err.status === 404 && sessionById(useUpload.getState().sessions, sid)) {
        useUpload.getState().cancel(sid);
        toast('The study this upload was going into was deleted.');
      }
      // Anything else: the next tick asks again.
    } finally {
      busy.delete(sid);
    }
    schedule();
  };
  const schedule = () => {
    const sessions = useUpload.getState().sessions;
    for (const [sid, t] of timers) {
      const s = sessionById(sessions, sid);
      if (!s || !needsPolling(s)) {
        clearTimeout(t);
        timers.delete(sid);
      }
    }
    for (const s of sessions) {
      if (timers.has(s.id) || busy.has(s.id) || !needsPolling(s)) continue;
      timers.set(s.id, setTimeout(() => void tick(s.id), watching(targetStudy(s)!) ? POLL_MS : SLOW_POLL_MS));
    }
  };
  useUpload.subscribe((st, prev) => {
    if (st.sessions !== prev.sessions) schedule();
  });
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      for (const s of useUpload.getState().sessions) if (needsPolling(s) && !busy.has(s.id)) timers.set(s.id, setTimeout(() => void tick(s.id), 0));
    });
  }
  schedule();
}

// Files refused for want of storage go by themselves once they fit: on the
// moments room is made here (retryRefused's callers), and every minute from a
// visible tab, for room made anywhere else (another tab, another device, an
// import finishing that this tab does not watch).
const ROOM_EVERY_MS = 60_000;
if (typeof window !== 'undefined') {
  const check = () => {
    if (document.visibilityState === 'visible' && refusedForRoom(useUpload.getState().sessions).length) void useUpload.getState().retryRefused();
  };
  setInterval(check, ROOM_EVERY_MS);
  document.addEventListener('visibilitychange', check);
}

// The sessions are kept across a refresh: written at most once a second while
// they change (a debounce reset by every progress patch never fired during a
// transfer), on the way out of the page, and removed the instant the last ends.
const SAVE_EVERY_MS = 1000;
{
  let timer: ReturnType<typeof setTimeout> | undefined;
  const saveNow = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    saveSessions(useUpload.getState().sessions);
  };
  useUpload.subscribe((s, prev) => {
    if (s.sessions === prev.sessions) return;
    if (!s.sessions.length) {
      saveNow();
      return;
    }
    if (!timer) timer = setTimeout(saveNow, SAVE_EVERY_MS);
  });
  if (typeof window !== 'undefined') window.addEventListener('pagehide', saveNow);
}
// Details a reload cut before they were saved go now.
void useUpload.getState().flushDetails();
